<script setup>
import axios from "@axios"
import {useAppAbility} from "@/plugins/casl/useAppAbility"
import { useDesignSystem } from '@/composables/useDesignSystem'

const emit = defineEmits([
  'submit',
])

// Ability
const ability = useAppAbility()

import moment from 'moment'

import {useUsersStore} from '@/stores'
import {requiredValidator} from "@validators"
import appStoreImage from "@/assets/app-store2.svg"
const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const usersStore = useUsersStore()
let myUser = null
let isShowingVerificationDialog = null
let isRunningInIOSAppStore = null;
({ myUser, isShowingVerificationDialog, isRunningInIOSAppStore } = storeToRefs(usersStore))

const code = ref('')
const firstName = ref('')
const lastName = ref('')
const referredBy = ref(myUser.value.primaryGroupName || myUser.value.referredBy)
const disableReferredBy = ref(false)

//computed property for platinumActivationModeOnlyNoVerify
const platinumActivationModeOnlyNoVerify = computed(() => {
  return myUser.value.pendingPlatinumApproval && myUser.value.platinumPlanIdToGiveAfterPlatinumApproval
})

watchEffect(() => {
  referredBy.value = myUser.value.primaryGroupName || myUser.value.referredBy
  if(referredBy.value){
    disableReferredBy.value = true
    myUser.value.verificationInfo1Data = referredBy.value
  }
})

const newCodeRequested = ref(false)

// Form Errors
const errors = ref({
  email: undefined,
  password: undefined,
  termsAccepted: undefined
})

const goToProfile = async () => {
  myUser.value.promptToVerify = false; await usersStore.updateMyUser(); await usersStore.hideVerificationDialog(); await usersStore.editMyUser()
}
</script>

<template>
  <VDialog
    :width="dWidth"
    :model-value="isShowingVerificationDialog"
    persistent
  >
    <VCard
      v-if="myUser?.pendingPlatinumApproval && myUser?.platinumPlanIdToGiveAfterPlatinumApproval && myUser?.verificationInfo1Data && !myUser?.promptToVerify"
      class="pa-5 pa-sm-15"
    >
      <!-- 👉 Title -->
      <VCardItem class="text-center pb-0 pt-0">
        <VCardTitle class="text-h5 font-weight-medium mb-2">
          Verification pending
        </VCardTitle>
        <VCardText v-if="myUser.verificationInfo1Data">
          We’ll review & confirm soon with {{ myUser.verificationInfo1Data }}
        </VCardText>
        <VCardText v-else>
          We’ll review & confirm soon
        </VCardText>
        <VCardText>
          To expedite approval, complete your profile<br>
          <VBtn
            width="fit-content"
            class="profile-header-edit-button mt-4 pl-1 pr-1"
            variant="text"
            prepend-icon="mdi-edit"
            density="compact"
            @click="goToProfile"
          >
            Complete Profile
          </VBtn>
        </VCardText>
      </VCardItem>

      <VCardText class="mt-0">
        <VForm @submit.prevent="() => {}">
          <VRow>
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <!--              <VBtn -->
              <!--                  color="secondary" -->
              <!--                  variant="tonal" -->
              <!--                  @click="myUser.promptToVerify = false; myUser.pendingVerification = false; usersStore.updateMyUser(); usersStore.hideVerificationDialog();" -->
              <!--              > -->
              <!--                Cancel Verification -->
              <!--              </VBtn> -->
              <VBtn
                color="secondary"
                variant="tonal"
                @click="myUser.promptToVerify = false; usersStore.updateMyUser(); usersStore.hideVerificationDialog();"
              >
                Close
              </VBtn>
            </VCol>

            <VCardText class="text-caption text-center">
              Once confirmed, your free Platinum membership will be activated
            </VCardText>
          </VRow>
          <div class="ma-auto text-center">
            <a
              v-if="!isRunningInIOSAppStore"
              href="https://apps.apple.com/us/app/rm-preferred-reservations/id6475874605"
              target="_blank"
            >
              <img
                :src="appStoreImage"
                class="mt-4 "
                height="40"
              >
            </a>
          </div>
        </VForm>
      </VCardText>
    </VCard>
    <VCard
      v-else-if="!myUser.pendingVerification"
      class="pa-5 pa-sm-15"
    >
      <!-- 👉 Title -->
      <VCardItem class="text-center pb-0 pt-0">
        <!--        <VCardTitle class="text-h5 font-weight-medium mb-2"> -->
        <!--          Verification -->
        <!--        </VCardTitle> -->


        <VCardText v-if="myUser?.platinumPlanIdToGiveAfterPlatinumApproval && myUser?.platinumPlanIdToGiveAfterPlatinumApproval.includes('enterprise-1cycle-3month')">
          Free Platinum membership through {{ moment().add(3,'month').format('M/D/YY') }}<br>
          <span v-if="myUser.verifierName">
            Provided by {{ myUser.verifierName }}
          </span>
        </VCardText>
        <VCardText v-else-if="platinumActivationModeOnlyNoVerify && myUser?.platinumPlanIdToGiveAfterPlatinumApproval">
          Free Platinum membership through {{ moment(myUser.platinumPlanIdToGiveAfterPlatinumApproval.slice(-10)).format('M/D/YY') }}<br>
          <span v-if="myUser.verifierName">
            Gifted by {{ myUser.verifierName }}
          </span>
        </VCardText>
        <VCardText v-else-if="myUser?.platinumPlanIdToGiveAfterVerification && myUser?.platinumPlanIdToGiveAfterVerification.includes('fixed-end')">
          <span><VIcon class="verified-icon">material-symbols:verified</VIcon>Verified</span> profile + free Platinum membership through {{ moment(myUser.platinumPlanIdToGiveAfterVerification.slice(-10)).format('M/D/YY') }}<br>
          <span v-if="myUser.verifierName">
            Gifted by {{ myUser.verifierName }}
          </span>
        </VCardText>
        <VCardText v-else-if="myUser?.platinumPlanIdToGiveAfterVerification == 'individual-1cycle-annual'">
          <span><VIcon class="verified-icon">material-symbols:verified</VIcon></span> profile + 1 year free of Platinum<br>
          <span v-if="myUser.verifierName">
            Gifted by {{ myUser.verifierName }}
          </span>
        </VCardText>
        <VCardText v-else-if="myUser?.verificationType == 'select'">
          <span><VIcon class="verified-icon">material-symbols:verified</VIcon></span> profile + 2 months free of Platinum<br>
          <span v-if="myUser.verifierName">
            Gifted by {{ myUser.verifierName }}
          </span>
        </VCardText>
        <VCardText v-else-if="myUser?.platinumPlanIdToGiveAfterVerification">
          <span><VIcon class="verified-icon">material-symbols:verified</VIcon>Verified</span> profile + free 3 month Platinum membership<br>
          <span v-if="myUser.verifierName">
            Gifted by {{ myUser.verifierName }}
          </span>
        </VCardText>
        <VCardText v-else>
          Submit for <span><VIcon class="verified-icon">material-symbols:verified</VIcon>Verified</span> profile <br>
        </VCardText>
      </VCardItem>

      <VCardText class="mt-0">
        <VForm @submit.prevent="() => {}">
          <VRow>
            <VCol
              v-if="myUser.verifierName && !platinumActivationModeOnlyNoVerify"
              cols="12"
            />
            <VCol
              v-else-if="myUser.verificationType == 'verified'"
              cols="12"
            >
              <VTextField
                v-model="myUser.verificationInfo1Data"
                :label="myUser.verificationInfo1Label"
                type="string"
                :error-messages="errors.code"
                :rules="[requiredValidator]"
                required
                hint="Name of Advisor, RM Member, Team, Union or Other referral source - we’ll confirm with them to verify you"
                persistent-hint
              />

              <VTextField
                v-model="myUser.verificationInfo2Data"
                :label="myUser.verificationInfo2Label"
                type="string"
                hint="Please share your Team, League or Industry  (movies, music, television, etc.)?"
                persistent-hint
              />
            </VCol>
            <VCol
              v-else-if="myUser.verificationType == 'select'"
              cols="12"
            >
              <VTextField
                v-model="myUser.verificationInfo1Data"
                :label="myUser.verificationInfo1Label"
                type="string"
                :error-messages="errors.code"
                :rules="[requiredValidator]"
                required
                hint="Please be specific - ie name of Chapter, Advisor, etc. "
                persistent-hint
              />

              <VTextField
                v-model="myUser.verificationInfo2Data"
                :label="myUser.verificationInfo2Label"
                type="string"
                :error-messages="errors.code"
                :rules="[requiredValidator]"
                required
                hint="Name of group contact (manager, leader, etc.) - we’ll confirm with them to verify you"
                persistent-hint
              />

              <!--              <VSelect -->
              <!--                v-model="myUser.verificationInfo2Data" -->
              <!--                :label="myUser.verificationInfo2Label" -->
              <!--                :error-messages="errors.code" -->
              <!--                :rules="[requiredValidator]" -->
              <!--                :items="['TIGER 21', 'YPO' ]" -->
              <!--                required -->
              <!--                hint="If multiple, please select the one who referred RM to you" -->
              <!--                persistent-hint -->
              <!--              /> -->
            </VCol>
            <VCol
              v-else-if="myUser.verificationType == 'platinumtrial'"
              cols="12"
            >
              <VTextField
                v-model="myUser.verificationInfo1Data"
                :label="myUser.verificationInfo1Label"
                type="string"
                :error-messages="errors.code"
                :rules="[requiredValidator]"
                required
                hint="Person &/or organization - we’ll confirm with them to verify you"
                persistent-hint
              />
            </VCol>
            <VCol
              v-else-if="myUser.verificationType == 'partner'"
              cols="12"
            >
              <VTextField
                v-model="myUser.verificationInfo1Data"
                :label="myUser.verificationInfo1Label"
                type="string"
                :error-messages="errors.code"
                :rules="[requiredValidator]"
                required
                hint="Name of team member/owner - we’ll confirm with them to verify you"
                persistent-hint
              />

              <VTextField
                v-model="myUser.verificationInfo2Data"
                :label="myUser.verificationInfo2Label"
                type="string"
                hint="If you’re an investor in &/or VIP of multiple, please state which one referred RM to you"
                persistent-hint
              />
            </VCol>
            <VCol
              v-else
              cols="12"
            >
              <VTextField
                v-model="myUser.profileLinkedInProfileId"
                label="LinkedIn"
                type="string"
                :error-messages="errors.code"
                prepend-inner-icon="mdi-linkedin"
                prefix="linkedin.com/in/"
                hint="enter profile id"
              />
              <VTextField
                v-model="myUser.profileInstagramProfileId"
                label="Instagram"
                type="string"
                :error-messages="errors.code"
                prepend-inner-icon="mdi-instagram"
                hint="enter username"
                class="mt-4"
                prefix="@"
              />
              <VTextField
                v-model="myUser.profileXProfileId"
                label="X"
                type="string"
                :error-messages="errors.code"
                prepend-inner-icon="simple-icons:x"
                hint="enter handle/username"
                class="mt-4"
                prefix="@"
              />
              <VTextField
                v-model="myUser.profileTikTokProfileId"
                label="TikTok"
                type="string"
                :error-messages="errors.code"
                prepend-inner-icon="simple-icons:tiktok"
                hint="enter username"
                class="mt-4"
                prefix="@"
              />
              <VTextField
                v-model="myUser.profileWebsiteLink"
                label="Bio page"
                type="string"
                :error-messages="errors.code"
                prepend-inner-icon="mdi-web"
                hint="enter full url to company/personal website bio"
                class="mt-4"
              />
            </VCol>

            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                v-if="platinumActivationModeOnlyNoVerify"
                type="submit"
                :disabled="(myUser?.verificationInfo1Label && !myUser?.verificationInfo1Data) || (myUser?.verificationInfo2Label && !myUser?.verificationInfo2Data)"
                @click="myUser.promptToVerify = false; usersStore.updateMyUser(); "
              >
                Submit for Verification
              </VBtn>
              <VBtn
                v-else
                type="submit"
                :disabled="(myUser?.verificationInfo1Label && !myUser?.verificationInfo1Data) || (myUser?.verificationInfo2Label && !myUser?.verificationInfo2Data)"
                @click="myUser.pendingVerification = true; usersStore.updateMyUser();"
              >
                Submit for Verification
              </VBtn>
              <VBtn
                color="secondary"
                variant="tonal"
                @click="myUser.promptToVerify = false; usersStore.updateMyUser(); usersStore.hideVerificationDialog();"
              >
                Cancel Verification
              </VBtn>
              <VBtn
                color="secondary"
                variant="tonal"
                @click="myUser.promptToVerify = false; usersStore.hideVerificationDialog();"
              >
                Continue Later
              </VBtn>
            </VCol>

            <VCardText
              v-if="platinumActivationModeOnlyNoVerify"
              class="text-caption text-center"
            >
              Once confirmed, your Platinum membership will be activated
            </VCardText>

            <VCardText
              v-else
              class=" text-center"
            >
              Once confirmed{{ (myUser.verifierName ? ' with ' + myUser.verifierName.split(' ')[0] : '') }}, your <span><VIcon class="verified-icon">material-symbols:verified</VIcon></span> profile + free Platinum membership will be activated<br>
            </VCardText>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>

    <VCard
      v-else
      class="pa-5 pa-sm-15"
    >
      <!-- 👉 Title -->
      <VCardItem class="text-center pb-0 pt-0">
        <VCardTitle class="text-h5 font-weight-medium mb-2">
          Verification pending
        </VCardTitle>
        <VCardText v-if="myUser.verifierName">
          We’ll review & confirm soon with {{ myUser.verifierName.split(' ')[0] }}
        </VCardText>
        <VCardText v-else-if="myUser.verificationInfo1Data">
          We’ll review & confirm soon with {{ myUser.verificationInfo1Data }}
        </VCardText>
        <VCardText v-else>
          We’ll review & confirm soon
        </VCardText>
        <VCardText>
          To expedite approval, complete your profile<br>
          <VBtn
            width="fit-content"
            class="profile-header-edit-button mt-4 pl-1 pr-1"
            variant="text"
            prepend-icon="mdi-edit"
            density="compact"
            @click="goToProfile"
          >
            Complete Profile
          </VBtn>
        </VCardText>
      </VCardItem>

      <VCardText class="mt-0">
        <VForm @submit.prevent="() => {}">
          <VRow>
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <!--              <VBtn -->
              <!--                  color="secondary" -->
              <!--                  variant="tonal" -->
              <!--                  @click="myUser.promptToVerify = false; myUser.pendingVerification = false; usersStore.updateMyUser(); usersStore.hideVerificationDialog();" -->
              <!--              > -->
              <!--                Cancel Verification -->
              <!--              </VBtn> -->
              <VBtn
                color="secondary"
                variant="tonal"
                @click="myUser.promptToVerify = false; usersStore.updateMyUser(); usersStore.hideVerificationDialog();"
              >
                Close
              </VBtn>
            </VCol>

            <VCardText class="text-caption text-center">
              Once confirmed, your <span><VIcon class="verified-icon">material-symbols:verified</VIcon> profile + free Platinum membership will be activated</span>
            </VCardText>
          </VRow>
          <div class="ma-auto text-center">
            <a
              v-if="!isRunningInIOSAppStore"
              href="https://apps.apple.com/us/app/rm-preferred-reservations/id6475874605"
              target="_blank"
            >
              <img
                :src="appStoreImage"
                class="mt-4 "
                height="40"
              >
            </a>
          </div>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.verified-icon{
  color: rgb(0, 149, 246);
  width: 1.25rem;
  height: 1.25rem;
  margin-bottom: 5px;
}
</style>
